import React, { useState, useEffect } from 'react';
import { Link, useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import Confetti from 'react-confetti';
import HandwritingCanvas from '@/components/HandwritingCanvas';
import { useElevenLabsTTS } from '@/hooks/useElevenLabsTTS';
import {
  ArrowLeft, ArrowRight, BookOpen, Calculator, CheckCircle, XCircle,
  Star, Trophy, Play, Volume2, Loader2, Target, Lightbulb, GraduationCap,
  ChevronRight, Award, TrendingUp, RotateCcw, Home, Pencil, FlaskConical
} from 'lucide-react';

// Phase states: placement -> instruction -> practice -> assessment -> results
const PHASES = ['placement', 'instruction', 'practice', 'assessment', 'results'];

const AdaptiveLearningPage = () => {
  const { childId, subject } = useParams();
  const navigate = useNavigate();
  const { api } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [child, setChild] = useState(null);
  const [needsPlacement, setNeedsPlacement] = useState(false);
  const [phase, setPhase] = useState('checking');
  const [currentLevel, setCurrentLevel] = useState(1);
  
  // Placement test state
  const [placementTest, setPlacementTest] = useState(null);
  const [placementQuestions, setPlacementQuestions] = useState([]);
  const [placementIndex, setPlacementIndex] = useState(0);
  
  // Lesson state
  const [lesson, setLesson] = useState(null);
  const [practiceQuestions, setPracticeQuestions] = useState([]);
  const [assessmentQuestions, setAssessmentQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [answeredCount, setAnsweredCount] = useState(0);
  
  // UI state
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [explanation, setExplanation] = useState('');
  const [showConfetti, setShowConfetti] = useState(false);
  const [results, setResults] = useState(null);

  const subjectConfig = {
    reading: { icon: BookOpen, color: '#10B981', name: 'Reading' },
    math: { icon: Calculator, color: '#EF4444', name: 'Math' },
    writing: { icon: Pencil, color: '#06B6D4', name: 'Writing' },
    science: { icon: FlaskConical, color: '#8B5CF6', name: 'Science' }
  };
  const config = subjectConfig[subject] || subjectConfig.reading;

  // Track if this is a writing question (for HandwritingCanvas)
  const [writingMode, setWritingMode] = useState(false);

  useEffect(() => {
    checkPlacement();
  }, [childId, subject]);

  const checkPlacement = async () => {
    try {
      const [childRes, placementRes] = await Promise.all([
        api.get(`/children/${childId}`),
        api.get(`/adaptive/check-placement/${childId}/${subject}`)
      ]);
      
      setChild(childRes.data);
      setCurrentLevel(placementRes.data.current_level);
      
      if (placementRes.data.needs_placement) {
        setNeedsPlacement(true);
        setPhase('placement-intro');
      } else {
        setPhase('instruction');
        loadLesson(placementRes.data.current_level);
      }
    } catch (error) {
      console.error('Failed to check placement:', error);
      toast.error('Failed to load adaptive learning');
    } finally {
      setLoading(false);
    }
  };

  const startPlacementTest = async () => {
    setLoading(true);
    try {
      const res = await api.post(`/adaptive/placement-test/${childId}/${subject}`);
      setPlacementTest(res.data.test_id);
      setPlacementQuestions(res.data.questions);
      setPlacementIndex(0);
      setPhase('placement');
    } catch (error) {
      toast.error('Failed to start placement test');
    } finally {
      setLoading(false);
    }
  };

  const submitPlacementAnswer = async (answer) => {
    setSelectedAnswer(answer);
    
    try {
      const question = placementQuestions[placementIndex];
      const res = await api.post(`/adaptive/placement/submit/${placementTest}`, {
        question_id: question.id,
        answer: answer,
        time_taken_seconds: 30
      });
      
      setIsCorrect(res.data.correct);
      setExplanation(res.data.explanation);
      setShowFeedback(true);
      
      setTimeout(() => {
        setShowFeedback(false);
        setSelectedAnswer(null);
        
        if (placementIndex < placementQuestions.length - 1) {
          setPlacementIndex(placementIndex + 1);
        } else {
          completePlacement();
        }
      }, 2000);
    } catch (error) {
      toast.error('Failed to submit answer');
    }
  };

  const completePlacement = async () => {
    setLoading(true);
    try {
      const res = await api.post(`/adaptive/placement/complete/${placementTest}`);
      setCurrentLevel(res.data.recommended_level);
      toast.success(res.data.message);
      
      // Load lesson for recommended level
      setPhase('instruction');
      loadLesson(res.data.recommended_level);
    } catch (error) {
      toast.error('Failed to complete placement');
    } finally {
      setLoading(false);
    }
  };

  const loadLesson = async (level) => {
    try {
      const res = await api.get(`/adaptive/lesson/${childId}/${subject}`);
      setLesson(res.data.lesson);
      setPracticeQuestions(res.data.practice_questions);
      setAssessmentQuestions(res.data.assessment_questions);
      setCurrentLevel(res.data.level);
    } catch (error) {
      console.error('Failed to load lesson:', error);
    }
  };

  const handleInstructionComplete = async () => {
    try {
      await api.post(`/adaptive/lesson/${childId}/${subject}/progress`, {
        phase: 'instruction',
        time_spent: 120
      });
      setCurrentQuestionIndex(0);
      setScore(0);
      setAnsweredCount(0);
      setPhase('practice');
    } catch (error) {
      console.error('Failed to update progress:', error);
    }
  };

  const handlePracticeComplete = () => {
    setCurrentQuestionIndex(0);
    setScore(0);
    setAnsweredCount(0);
    setPhase('assessment');
  };

  const submitAnswer = async (answer, isPractice = false) => {
    setSelectedAnswer(answer);
    
    const questions = isPractice ? practiceQuestions : assessmentQuestions;
    const question = questions[currentQuestionIndex];
    
    try {
      const res = await api.post(`/adaptive/lesson/${childId}/${subject}/progress`, {
        phase: isPractice ? 'practice' : 'assessment',
        question_id: question.id,
        answer: answer,
        time_spent: 30
      });
      
      const correct = res.data.correct;
      setIsCorrect(correct);
      setExplanation(res.data.explanation);
      setShowFeedback(true);
      
      if (correct) {
        setScore(score + 1);
      }
      setAnsweredCount(answeredCount + 1);
      
      setTimeout(() => {
        setShowFeedback(false);
        setSelectedAnswer(null);
        
        if (currentQuestionIndex < questions.length - 1) {
          setCurrentQuestionIndex(currentQuestionIndex + 1);
        } else if (isPractice) {
          handlePracticeComplete();
        } else {
          completeAssessment();
        }
      }, 2000);
    } catch (error) {
      toast.error('Failed to submit answer');
    }
  };

  const completeAssessment = async () => {
    setLoading(true);
    try {
      const res = await api.post(`/adaptive/lesson/${childId}/${subject}/complete`);
      setResults(res.data);
      setPhase('results');
      
      if (res.data.passed) {
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 5000);
        
        // Auto-complete the daily task if this lesson was started from a daily task
        const activeTaskId = localStorage.getItem('activeTaskId');
        const activeTaskModule = localStorage.getItem('activeTaskModule');
        
        if (activeTaskId && activeTaskModule === subject) {
          try {
            await api.post(`/daily-tasks/${activeTaskId}/complete`);
            toast.success('Daily task completed! +XP earned!', {
              icon: '🎯',
              duration: 3000
            });
            // Clear the stored task
            localStorage.removeItem('activeTaskId');
            localStorage.removeItem('activeTaskModule');
          } catch (taskError) {
            console.log('Task may already be completed:', taskError);
          }
        }
      }
    } catch (error) {
      toast.error('Failed to complete assessment');
    } finally {
      setLoading(false);
    }
  };

  const restartLesson = () => {
    setPhase('instruction');
    setCurrentQuestionIndex(0);
    setScore(0);
    setAnsweredCount(0);
    setResults(null);
    loadLesson(currentLevel);
  };

  const { speak: speakText } = useElevenLabsTTS({ voice: 'nova', speed: 0.9 });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-slate-50 to-white">
        <Loader2 className="w-12 h-12 animate-spin" style={{ color: config.color }} />
      </div>
    );
  }

  // PLACEMENT INTRO
  if (phase === 'placement-intro') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-indigo-50 to-white">
        <header className="bg-white/80 backdrop-blur-md border-b sticky top-0 z-50">
          <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
            <Link to={`/learn/${childId}`}>
              <Button variant="ghost" size="icon" className="rounded-full">
                <ArrowLeft className="w-5 h-5" />
              </Button>
            </Link>
            <h1 className="font-fredoka text-lg font-bold">{config.name} Placement Test</h1>
            <Target className="w-6 h-6" style={{ color: config.color }} />
          </div>
        </header>

        <main className="max-w-2xl mx-auto px-4 py-12 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="w-24 h-24 mx-auto mb-6 rounded-full flex items-center justify-center" style={{ backgroundColor: `${config.color}20` }}>
              <config.icon className="w-12 h-12" style={{ color: config.color }} />
            </div>
            
            <h1 className="font-fredoka text-3xl font-bold text-slate-800 mb-4">
              Let's Find Your Level!
            </h1>
            <p className="text-lg text-slate-600 mb-8 max-w-md mx-auto">
              Before we start, let's see what you already know. This helps us give you the perfect lessons!
            </p>

            <Card className="mb-8 text-left">
              <CardContent className="p-6">
                <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                  <Lightbulb className="w-5 h-5 text-amber-500" />
                  What to expect:
                </h3>
                <ul className="space-y-3 text-slate-600">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-500 shrink-0 mt-0.5" />
                    <span>15 quick questions to understand your skills</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-500 shrink-0 mt-0.5" />
                    <span>Questions get easier or harder based on your answers</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-500 shrink-0 mt-0.5" />
                    <span>You'll be placed at the perfect starting level</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Button
              size="lg"
              className="h-16 px-12 text-lg rounded-full"
              style={{ backgroundColor: config.color }}
              onClick={startPlacementTest}
              data-testid="start-placement-btn"
            >
              <Play className="w-6 h-6 mr-2" />
              Start Placement Test
            </Button>
          </motion.div>
        </main>
      </div>
    );
  }

  // PLACEMENT TEST
  if (phase === 'placement') {
    const question = placementQuestions[placementIndex];
    const progress = ((placementIndex + 1) / placementQuestions.length) * 100;

    return (
      <div className="min-h-screen bg-gradient-to-b from-indigo-50 to-white">
        <header className="bg-white/80 backdrop-blur-md border-b sticky top-0 z-50">
          <div className="max-w-3xl mx-auto px-4 py-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-slate-600">Placement Test</span>
              <span className="text-sm text-slate-500">
                Question {placementIndex + 1} of {placementQuestions.length}
              </span>
            </div>
            <Progress value={progress} className="h-2" />
          </div>
        </header>

        <main className="max-w-3xl mx-auto px-4 py-8">
          <AnimatePresence mode="wait">
            <motion.div
              key={placementIndex}
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
            >
              <Card className="mb-6">
                <CardContent className="p-8">
                  <div className="flex items-center justify-between mb-6">
                    <span className="text-sm px-3 py-1 rounded-full bg-slate-100 text-slate-600">
                      Level {question.level}
                    </span>
                    <Button variant="ghost" size="sm" onClick={() => speakText(question.question)}>
                      <Volume2 className="w-4 h-4 mr-1" /> Listen
                    </Button>
                  </div>
                  
                  <h2 className="font-fredoka text-2xl font-bold text-slate-800 text-center mb-8">
                    {question.question}
                  </h2>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {question.options.map((option, idx) => {
                      const isSelected = selectedAnswer === option;
                      const isCorrectAnswer = option === question.answer;
                      
                      let buttonClass = 'h-16 text-lg font-medium rounded-xl border-2 transition-all ';
                      
                      if (showFeedback) {
                        if (isCorrectAnswer) {
                          buttonClass += 'bg-green-100 border-green-400 text-green-700';
                        } else if (isSelected) {
                          buttonClass += 'bg-red-100 border-red-400 text-red-700';
                        } else {
                          buttonClass += 'bg-slate-50 border-slate-200 text-slate-400';
                        }
                      } else {
                        buttonClass += 'bg-white border-slate-200 hover:border-indigo-400 hover:bg-indigo-50 text-slate-800';
                      }

                      return (
                        <Button
                          key={idx}
                          className={buttonClass}
                          onClick={() => !showFeedback && submitPlacementAnswer(option)}
                          disabled={showFeedback}
                          data-testid={`placement-option-${idx}`}
                        >
                          {showFeedback && isCorrectAnswer && <CheckCircle className="w-5 h-5 mr-2" />}
                          {showFeedback && isSelected && !isCorrectAnswer && <XCircle className="w-5 h-5 mr-2" />}
                          {option}
                        </Button>
                      );
                    })}
                  </div>

                  {showFeedback && explanation && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="mt-6 p-4 rounded-xl bg-slate-50"
                    >
                      <p className="text-sm text-slate-600">
                        <span className="font-semibold">Explanation:</span> {explanation}
                      </p>
                    </motion.div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          </AnimatePresence>
        </main>
      </div>
    );
  }

  // INSTRUCTION PHASE
  if (phase === 'instruction' && lesson) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
        <header className="bg-white/80 backdrop-blur-md border-b sticky top-0 z-50">
          <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
            <Link to={`/learn/${childId}`}>
              <Button variant="ghost" size="icon" className="rounded-full">
                <ArrowLeft className="w-5 h-5" />
              </Button>
            </Link>
            <div className="text-center">
              <h1 className="font-fredoka text-lg font-bold text-slate-800">{lesson.title}</h1>
              <p className="text-xs text-slate-500">Level {currentLevel}</p>
            </div>
            <GraduationCap className="w-6 h-6" style={{ color: config.color }} />
          </div>
        </header>

        <main className="max-w-4xl mx-auto px-4 py-8">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            {/* Intro */}
            <Card className="mb-6" style={{ borderTop: `4px solid ${config.color}` }}>
              <CardContent className="p-6">
                <h2 className="font-fredoka text-xl font-bold text-slate-800 mb-3">
                  {lesson.instruction?.introduction}
                </h2>
                <p className="text-slate-600">{lesson.description}</p>
              </CardContent>
            </Card>

            {/* Learning Objectives */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Target className="w-5 h-5" style={{ color: config.color }} />
                  What You'll Learn
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {lesson.objectives?.map((obj, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-slate-600">
                      <CheckCircle className="w-5 h-5 text-green-500 shrink-0 mt-0.5" />
                      <span>{obj}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Key Concepts */}
            <div className="grid md:grid-cols-2 gap-4 mb-6">
              {lesson.instruction?.key_concepts?.map((concept, idx) => (
                <Card key={idx} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-5">
                    <h3 className="font-semibold text-slate-800 mb-2 flex items-center gap-2">
                      <Lightbulb className="w-5 h-5 text-amber-500" />
                      {concept.concept}
                    </h3>
                    <p className="text-sm text-slate-600">{concept.explanation}</p>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Examples */}
            {lesson.instruction?.examples?.length > 0 && (
              <Card className="mb-6 bg-indigo-50 border-indigo-200">
                <CardContent className="p-5">
                  <h3 className="font-semibold text-indigo-800 mb-3">Examples:</h3>
                  <ul className="space-y-2">
                    {lesson.instruction.examples.map((ex, idx) => (
                      <li key={idx} className="text-indigo-700 text-sm">• {ex}</li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {/* Tips */}
            {lesson.instruction?.tips?.length > 0 && (
              <Card className="mb-8 bg-amber-50 border-amber-200">
                <CardContent className="p-5">
                  <h3 className="font-semibold text-amber-800 mb-3">💡 Tips:</h3>
                  <ul className="space-y-2">
                    {lesson.instruction.tips.map((tip, idx) => (
                      <li key={idx} className="text-amber-700 text-sm">• {tip}</li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            <div className="text-center">
              <Button
                size="lg"
                className="h-14 px-10 text-lg rounded-full"
                style={{ backgroundColor: config.color }}
                onClick={handleInstructionComplete}
                data-testid="start-practice-btn"
              >
                Start Practice
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </motion.div>
        </main>
      </div>
    );
  }

  // PRACTICE OR ASSESSMENT PHASE
  if (phase === 'practice' || phase === 'assessment') {
    const isPractice = phase === 'practice';
    const questions = isPractice ? practiceQuestions : assessmentQuestions;
    const question = questions[currentQuestionIndex];
    const progress = ((currentQuestionIndex + 1) / questions.length) * 100;

    return (
      <div className="min-h-screen" style={{ backgroundColor: isPractice ? '#F0FDF4' : '#FEF9C3' }}>
        <header className="bg-white/90 backdrop-blur-md border-b sticky top-0 z-50">
          <div className="max-w-3xl mx-auto px-4 py-3">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-3">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  isPractice ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                }`}>
                  {isPractice ? '🎯 Practice' : '📝 Assessment'}
                </span>
                <span className="text-sm text-slate-600">Level {currentLevel}</span>
              </div>
              <div className="flex items-center gap-2">
                <Star className="w-5 h-5 text-yellow-500" />
                <span className="font-bold">{score}/{answeredCount}</span>
              </div>
            </div>
            <Progress value={progress} className="h-2" />
            <p className="text-xs text-center text-slate-500 mt-1">
              Question {currentQuestionIndex + 1} of {questions.length}
            </p>
          </div>
        </header>

        <main className="max-w-3xl mx-auto px-4 py-8">
          <AnimatePresence mode="wait">
            <motion.div
              key={`${phase}-${currentQuestionIndex}`}
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
            >
              <Card className="shadow-lg">
                <CardContent className="p-8">
                  <div className="flex justify-end mb-4">
                    <Button variant="ghost" size="sm" onClick={() => speakText(question.question)}>
                      <Volume2 className="w-4 h-4 mr-1" /> Listen
                    </Button>
                  </div>
                  
                  <h2 className="font-fredoka text-2xl font-bold text-slate-800 text-center mb-8">
                    {question.question}
                  </h2>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {question.options.map((option, idx) => {
                      const isSelected = selectedAnswer === option;
                      const isCorrectAnswer = option === question.answer;
                      
                      let buttonClass = 'h-16 text-lg font-medium rounded-xl border-2 transition-all ';
                      
                      if (showFeedback) {
                        if (isCorrectAnswer) {
                          buttonClass += 'bg-green-100 border-green-400 text-green-700';
                        } else if (isSelected) {
                          buttonClass += 'bg-red-100 border-red-400 text-red-700';
                        } else {
                          buttonClass += 'bg-slate-50 border-slate-200 text-slate-400';
                        }
                      } else {
                        buttonClass += `bg-white border-slate-200 text-slate-800 hover:border-${isPractice ? 'green' : 'amber'}-400`;
                      }

                      return (
                        <Button
                          key={idx}
                          className={buttonClass}
                          onClick={() => !showFeedback && submitAnswer(option, isPractice)}
                          disabled={showFeedback}
                          data-testid={`${phase}-option-${idx}`}
                        >
                          {showFeedback && isCorrectAnswer && <CheckCircle className="w-5 h-5 mr-2" />}
                          {showFeedback && isSelected && !isCorrectAnswer && <XCircle className="w-5 h-5 mr-2" />}
                          {option}
                        </Button>
                      );
                    })}
                  </div>

                  {showFeedback && explanation && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`mt-6 p-4 rounded-xl ${isCorrect ? 'bg-green-50' : 'bg-amber-50'}`}
                    >
                      <p className="text-sm text-slate-700">
                        <span className="font-semibold">
                          {isCorrect ? '✓ Correct! ' : '✗ Not quite. '}
                        </span>
                        {explanation}
                      </p>
                    </motion.div>
                  )}

                  {question.hint && !showFeedback && (
                    <p className="text-center text-sm text-slate-500 mt-6">
                      💡 Hint: {question.hint}
                    </p>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          </AnimatePresence>
        </main>
      </div>
    );
  }

  // RESULTS PHASE
  if (phase === 'results' && results) {
    const passed = results.passed;

    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center p-4">
        {showConfetti && <Confetti recycle={false} numberOfPieces={200} />}
        
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="w-full max-w-md"
        >
          <Card className="shadow-2xl">
            <CardContent className="p-8 text-center">
              <div className={`w-24 h-24 mx-auto mb-6 rounded-full flex items-center justify-center ${
                passed ? 'bg-green-100' : 'bg-amber-100'
              }`}>
                {passed ? (
                  <Trophy className="w-12 h-12 text-yellow-500" />
                ) : (
                  <TrendingUp className="w-12 h-12 text-amber-500" />
                )}
              </div>
              
              <h2 className="font-fredoka text-3xl font-bold text-slate-800 mb-2">
                {passed ? 'Excellent Work!' : 'Keep Going!'}
              </h2>
              
              <p className="text-5xl font-bold mb-2" style={{ color: passed ? '#22C55E' : '#F59E0B' }}>
                {results.score}%
              </p>
              
              <p className="text-slate-600 mb-6">
                {results.questions_correct} out of {results.questions_total} correct
              </p>

              {/* Level info */}
              <div className="p-4 rounded-xl bg-slate-50 mb-6">
                {passed ? (
                  results.mastered ? (
                    <p className="text-slate-700">
                      <span className="font-semibold text-emerald-600">🏆 Chapter mastered!</span>{' '}
                      <span className="text-sm text-slate-600">
                        Starting Review Chapter {Math.max(1, (results.next_level ?? 0) - (results.content_cap ?? 3))}
                      </span>
                    </p>
                  ) : (
                    <p className="text-slate-700">
                      <span className="font-semibold text-green-600">🎉 You advanced to Level {results.next_level}!</span>
                    </p>
                  )
                ) : (
                  <p className="text-slate-700">
                    You need 80% to advance. Let's practice more!
                  </p>
                )}
              </div>

              {/* XP earned */}
              <div className="flex items-center justify-center gap-2 mb-6 text-amber-600">
                <Star className="w-5 h-5" />
                <span className="font-semibold">+{results.xp_earned} XP earned!</span>
              </div>

              {/* Weak/Strong areas */}
              {(results.weak_areas?.length > 0 || results.strong_areas?.length > 0) && (
                <div className="grid grid-cols-2 gap-4 mb-6 text-left">
                  {results.strong_areas?.length > 0 && (
                    <div className="p-3 rounded-lg bg-green-50">
                      <p className="text-xs font-semibold text-green-700 mb-1">💪 Strong Areas</p>
                      <p className="text-xs text-green-600">{results.strong_areas.join(', ')}</p>
                    </div>
                  )}
                  {results.weak_areas?.length > 0 && (
                    <div className="p-3 rounded-lg bg-amber-50">
                      <p className="text-xs font-semibold text-amber-700 mb-1">📚 Keep Practicing</p>
                      <p className="text-xs text-amber-600">{results.weak_areas.join(', ')}</p>
                    </div>
                  )}
                </div>
              )}

              <div className="space-y-3">
                {passed ? (
                  <Button
                    className="w-full h-14 text-lg"
                    style={{ backgroundColor: config.color }}
                    onClick={async () => {
                      await loadLesson(results.next_level);
                      setCurrentQuestionIndex(0);
                      setScore(0);
                      setAnsweredCount(0);
                      setResults(null);
                      setPhase('instruction');
                    }}
                    data-testid="next-level-btn"
                  >
                    <ArrowRight className="w-5 h-5 mr-2" />
                    {results.mastered ? `Start Review Chapter` : `Continue to Level ${results.next_level}`}
                  </Button>
                ) : (
                  <Button
                    className="w-full h-14 text-lg bg-amber-500 hover:bg-amber-600"
                    onClick={restartLesson}
                    data-testid="retry-lesson-btn"
                  >
                    <RotateCcw className="w-5 h-5 mr-2" />
                    Try Again
                  </Button>
                )}
                
                <Link to={`/learn/${childId}`}>
                  <Button variant="outline" className="w-full h-12">
                    <Home className="w-5 h-5 mr-2" />
                    Back to Learning Hub
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  return null;
};

export default AdaptiveLearningPage;
